// exchangeRateService.ts
// Public API-based live exchange rate fetcher with localStorage cache fallback

const CACHE_KEY = 'polatlar_exchange_rates_cache';
const SETTINGS_KEY = 'polatlar_exchange_rate_settings';

export interface ExchangeRates {
  usd_try: number;
  eur_try: number;
  gbp_try: number;
  gold_try: number | null;
  updated_at: string;
  provider: string;
  // Previous values for change % calculation (stored from previous fetch)
  prev_usd_try?: number;
  prev_eur_try?: number;
  prev_gbp_try?: number;
  prev_gold_try?: number | null;
}

export interface ExchangeRateSettings {
  auto_refresh: boolean;
  refresh_interval: number; // minutes: 1, 5, 15, 30
  provider: 'auto' | 'open-er-api' | 'frankfurter';
}

const DEFAULT_SETTINGS: ExchangeRateSettings = {
  auto_refresh: true,
  refresh_interval: 5,
  provider: 'auto',
};

function getSettings(): ExchangeRateSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_SETTINGS, ...parsed };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function saveExchangeRateSettings(settings: Partial<ExchangeRateSettings>): void {
  const current = getSettings();
  const updated = { ...current, ...settings };
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(updated));
  } catch (e) {
    console.error('Failed to save exchange rate settings', e);
  }
}

export function getExchangeRateSettings(): ExchangeRateSettings {
  return getSettings();
}

function saveCache(rates: ExchangeRates): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(rates));
  } catch (e) {
    console.error('Failed to save exchange rate cache', e);
  }
}

export function getCachedExchangeRates(): ExchangeRates | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.usd_try !== 'number') return null;
    return parsed as ExchangeRates;
  } catch {
    return null;
  }
}

// Fetch gold price in TRY from open.er-api (XAU is gold in troy ounce USD)
// 1 troy ounce = 31.1035 grams → gram gold = (XAU_USD_price / 31.1035) * USD_TRY
async function fetchGoldTry(usdTry: number): Promise<number | null> {
  try {
    const resp = await fetch('https://open.er-api.com/v6/latest/XAU');
    if (!resp.ok) return null;
    const data = await resp.json();
    if (!data?.rates?.USD || typeof data.rates.USD !== 'number') return null;
    const xauUsd = 1 / data.rates.USD; // USD per troy ounce
    const gramGoldTry = (xauUsd / 31.1035) * usdTry;
    return Math.round(gramGoldTry * 100) / 100;
  } catch {
    return null;
  }
}

async function fetchFromOpenErApi(): Promise<ExchangeRates | null> {
  const resp = await fetch('https://open.er-api.com/v6/latest/USD');
  if (!resp.ok) return null;
  const data = await resp.json();
  if (!data?.rates || typeof data.rates.TRY !== 'number' || typeof data.rates.EUR !== 'number' || typeof data.rates.GBP !== 'number') return null;

  const usdTry = data.rates.TRY;
  const usdEur = data.rates.EUR;
  const usdGbp = data.rates.GBP;

  const goldTry = await fetchGoldTry(usdTry);

  return {
    usd_try: Math.round(usdTry * 100) / 100,
    eur_try: Math.round((usdTry / usdEur) * 100) / 100,
    gbp_try: Math.round((usdTry / usdGbp) * 100) / 100,
    gold_try: goldTry,
    updated_at: new Date().toISOString(),
    provider: 'open-er-api',
  };
}

async function fetchFromFrankfurter(): Promise<ExchangeRates | null> {
  const resp = await fetch('https://api.frankfurter.app/latest?from=USD&to=TRY,EUR,GBP');
  if (!resp.ok) return null;
  const data = await resp.json();
  if (!data?.rates || typeof data.rates.TRY !== 'number' || typeof data.rates.EUR !== 'number' || typeof data.rates.GBP !== 'number') return null;

  const usdTry = data.rates.TRY;
  const usdEur = data.rates.EUR;
  const usdGbp = data.rates.GBP;

  const goldTry = await fetchGoldTry(usdTry);

  return {
    usd_try: Math.round(usdTry * 100) / 100,
    eur_try: Math.round((usdTry / usdEur) * 100) / 100,
    gbp_try: Math.round((usdTry / usdGbp) * 100) / 100,
    gold_try: goldTry,
    updated_at: new Date().toISOString(),
    provider: 'frankfurter',
  };
}

export async function fetchExchangeRates(): Promise<{ rates: ExchangeRates | null; fromCache: boolean; error: string | null }> {
  const settings = getSettings();
  const prevCache = getCachedExchangeRates();

  let providers: Array<() => Promise<ExchangeRates | null>>;

  switch (settings.provider) {
    case 'open-er-api':
      providers = [fetchFromOpenErApi, fetchFromFrankfurter];
      break;
    case 'frankfurter':
      providers = [fetchFromFrankfurter, fetchFromOpenErApi];
      break;
    case 'auto':
    default:
      providers = [fetchFromOpenErApi, fetchFromFrankfurter];
      break;
  }

  for (const fetcher of providers) {
    try {
      const rates = await fetcher();
      if (rates) {
        // Store previous values for change calculation
        if (prevCache) {
          rates.prev_usd_try = prevCache.usd_try;
          rates.prev_eur_try = prevCache.eur_try;
          rates.prev_gbp_try = prevCache.gbp_try;
          rates.prev_gold_try = prevCache.gold_try;
        }
        saveCache(rates);
        return { rates, fromCache: false, error: null };
      }
    } catch {
      // Try next provider
    }
  }

  // All providers failed, try cache
  const cached = getCachedExchangeRates();
  if (cached) {
    return { rates: cached, fromCache: true, error: null };
  }

  return { rates: null, fromCache: false, error: 'Döviz kurları şu anda alınamadı.' };
}